import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { AbstractWidgetComponent, ContextChange, PagedContentConfiguration, PagedContentType, WidgetStyle } from '@sdbn/appclient';
import { DataType, TableColumnDefinition, TranslationService } from '@sdbn/common';
import { interval, Subscription } from 'rxjs';
import { environment } from 'src/environments/environment';
import { ComponentSelectionStorageService } from 'src/tmc-common/services/component-selection-storage.service';
import { CMovementOrderTableRow, MovementOrderDetails, MovementOrderProcessingStatus, ServiceTruckOption, TruckGroup } from '../movement-orders.model';
import { MovementOrdersService } from '../movement-orders.service';

@Component({
  selector: 'app-movement-orders',
  templateUrl: './movement-orders.component.html',
  styleUrls: ['./movement-orders.component.scss']
})
export class MovementOrdersComponent extends AbstractWidgetComponent implements OnInit, OnDestroy {

  private STORAGE_KEY = 'movementorders.settings';

  configuration = new PagedContentConfiguration({
    selectionTitle: 'Bla bla',
    columns: [
      new TableColumnDefinition({
        dictionaryKey: 'tmc-yard.entity.moving-order.priority',
        type: DataType.STRING,
        identifier: 'priority'
      }),
      new TableColumnDefinition({
        dictionaryKey: 'tmc-yard.entity.moving-order.referenceDate',
        type: DataType.STRING,
        identifier: 'referenceDate'
      }),
      new TableColumnDefinition({
        dictionaryKey: 'tmc-yard.entity.moving-order.scope',
        type: DataType.STRING,
        identifier: 'scope'
      }),
      new TableColumnDefinition({
        dictionaryKey: 'tmc-yard.entity.moving-order.identifier',
        type: DataType.STRING,
        identifier: 'identifier'
      }),
      new TableColumnDefinition({
        dictionaryKey: 'tmc-yard.entity.moving-order.transportReference',
        type: DataType.STRING,
        identifier: 'transportReference'
      }),
      new TableColumnDefinition({
        dictionaryKey: 'tmc-yard.entity.moving-order.currentStation',
        type: DataType.STRING,
        identifier: 'currentStation'
      }),
      new TableColumnDefinition({
        dictionaryKey: 'tmc-yard.entity.moving-order.targetStation',
        type: DataType.STRING,
        identifier: 'targetStation'
      }),
      new TableColumnDefinition({
        dictionaryKey: 'tmc-yard.entity.moving-order.movementStrategy.label',
        type: DataType.STRING,
        identifier: 'movementStrategy',
        cellValueProvider: {
          getValue: (cell: CMovementOrderTableRow) => {
            if (cell.movementStrategy) {
              return this.translationService.translate('tmc-yard.entity.moving-order.movementStrategy.' + cell.movementStrategy);
            } else {
              return '';
            }
          }
        }
      }),
      new TableColumnDefinition({
        dictionaryKey: 'tmc-yard.entity.moving-order.processingStatus.label',
        type: DataType.STRING,
        identifier: 'processingStatus',
        cellValueProvider: {
          getValue: (cell: CMovementOrderTableRow) => {
            if (cell.processingStatus) {
              return this.translationService.translate('tmc-yard.entity.moving-order.processingStatus.' + cell.processingStatus);
            } else {
              return '';
            }
          }
        }
      })
    ],
    itemDataAccessor: undefined,
    desktopType: PagedContentType.TABLE_MENU_SINGLE,
    mobileType: PagedContentType.TABLE_MENU_SINGLE
  });


  truckGroupsEnabled = true;
  truckGroups = new Array<TruckGroup>();
  truckGroupsOfSelectedTruck = new Array<TruckGroup>();
  selectedTruckGroup?: TruckGroup;
  serviceTruckOptions = new Array<ServiceTruckOption>();
  selectedServiceTruck?: ServiceTruckOption;
  movementOrderDetails?: MovementOrderDetails;

  refreshEnabled = true;
  refreshInterval = 30000;
  refreshTimer!: Subscription;

  serviceTruckSelectionControl = new FormControl<ServiceTruckOption | undefined>(undefined, [Validators.required]);

  data = new Array<CMovementOrderTableRow>();
  selectedItem?: CMovementOrderTableRow;

  constructor(private movementOrdersService: MovementOrdersService,
    private componentSelectionStorageService: ComponentSelectionStorageService,
    public translationService: TranslationService) {
    super();

    this.movementOrdersService.getTruckGroups().subscribe(truckGroups => {
      this.truckGroups = truckGroups;

      this.movementOrdersService.loadServiceTruckOptions().subscribe(
        options => {
          this.serviceTruckOptions = options;
          if (this.serviceTruckOptions?.length > 0) {
            this.restoreServiceTruckSelectionFromStorage();
            this.serviceTruckSelectionControl.setValue(this.serviceTruckOptions[0], {emitEvent: false});
            this.selectedServiceTruck = this.serviceTruckOptions[0];
            this.setTruckGroupsForSelectedServiceTruck();
            this.loadMovementOrders().then(() => {
              this.storeSettings();
              this.applyFormListener();
            });
          }
        }
      );
    });


    this.data = new Array<CMovementOrderTableRow>();
  }

  ngOnInit(): void {
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    if (this.refreshTimer) {
      this.refreshTimer.unsubscribe();
      this.refreshTimer = null;
    }
  }

  onContextChange(contextChange: ContextChange) { }

  onSetElementAndContext() {
    this.refreshEnabled = this.widgetReference.refreshEnabled == null ? true : this.widgetReference.refreshEnabled;
    if (this.widgetReference.refreshInterval != null && this.widgetReference.refreshInterval > 0) {
      this.refreshInterval = this.widgetReference.refreshInterval * 1000;
    } else {
      this.refreshInterval = 30 * 1000;
    }
    this.registerRefreshTimer();
    this.restoreMovementOrderSelection();
  }

  onSelectionChanged(selectedItem: CMovementOrderTableRow) {
    console.log('selection changed', selectedItem);
    this.selectedItem = selectedItem;
    this.loadMovementOrderDetails();
    this.storeSettings();
  }

  selectTruckGroup(truckGroup: TruckGroup) {
    this.selectedTruckGroup = truckGroup;
    //this.performReloadOfMovementOrders();
  }


  onMovementOrderDetailsChanged(): void {
    this.loadMovementOrderDetails();
  }

  private setTruckGroupsForSelectedServiceTruck() {
    const groups = new Array<TruckGroup>();
    groups.push(...this.truckGroups.filter(tg => tg.trucks.indexOf(this.selectedServiceTruck.truckId) >= 0));
    this.truckGroupsOfSelectedTruck = groups;
    if (this.truckGroupsOfSelectedTruck.length > 0) {
      this.selectedTruckGroup = this.truckGroupsOfSelectedTruck[0];
    }
  }

  private applyFormListener() {
    this.serviceTruckSelectionControl.valueChanges.subscribe(
      v => {
        console.log('serviceTruckSelectionControl.valueChanged', v);
        this.selectedServiceTruck = v;
        this.storeSettings();
        this.setTruckGroupsForSelectedServiceTruck();
        this.loadMovementOrders();
      }
    );
  }

  private registerRefreshTimer() {

    if (this.refreshTimer) {
      this.refreshTimer.unsubscribe();
    }

    if (environment.autoreload && this.refreshEnabled) {
      this.refreshTimer = interval(this.refreshInterval).subscribe(() => this.loadMovementOrders());
    } else {
      console.log('Autoreload YardMapComponent disabled');
    }
  }

  private async loadMovementOrders() {
    if (this.selectedTruckGroup != null && this.selectedServiceTruck != null) {
      const orders = await this.movementOrdersService.loadMovementOrderTableRowsForTruckGroup(this.selectedServiceTruck.truckId, this.selectedTruckGroup.id).toPromise();

      // const restoredSelection = this.data.find(d => d.id === this.selectedItem);
      // this.toggleSelection(restoredSelection);
      const settings = this.componentSelectionStorageService.getWidgetSelection(this.STORAGE_KEY, this.widgetIdentifier);
      if (settings != null) { 
        if (settings.selectedMovementOrder != null) {
          orders.forEach((mv) => {
            if (mv.id === settings.selectedMovementOrder) {
              this.selectedItem = mv;
              return;
            }
          });
        }
      }
      this.data = orders;
    }
  }


  private loadMovementOrderDetails() {
    this.movementOrderDetails = undefined;
    if (this.selectedItem) {
      this.movementOrdersService.loadMovementOrderDetails(this.selectedItem!.id!)
        .subscribe(
          details => {
            this.movementOrderDetails = details;
            if (this.movementOrderDetails?.processingStatus === MovementOrderProcessingStatus.COMPLETED || this.movementOrderDetails?.processingStatus === MovementOrderProcessingStatus.CANCELED) {
              this.movementOrderDetails = undefined;
              this.data = [];
              this.selectedItem = undefined;
              console.log('clean data');
              setTimeout(() => {
                this.loadMovementOrders().then(() => console.log('----------loaded', this.data));
              }, 100);
            }
            setTimeout(() => this.registerRefreshTimer(), 300);
          }
        );
    }
  }


  private restoreServiceTruckSelectionFromStorage() {
    const settings = this.componentSelectionStorageService.getWidgetSelection(this.STORAGE_KEY, this.widgetIdentifier);
    if (settings != null) { 
      if (settings.selectedServiceTruck != null) {
        console.error('restore', settings, this.serviceTruckOptions);
        this.serviceTruckOptions.forEach(availableServiceTruckOptions => {
          if (availableServiceTruckOptions.truckId == settings.selectedServiceTruck.truckId) {
            this.serviceTruckSelectionControl.setValue(availableServiceTruckOptions);
          }
        });
      }
    }
  }

  private restoreMovementOrderSelection() {
    const settings = this.componentSelectionStorageService.getWidgetSelection(this.STORAGE_KEY, this.widgetIdentifier);
    if (settings != null) { 
      if (settings.selectedMovementOrder != null) {
        this.data.forEach((mv) => {
          if (mv.id === settings.selectedMovementOrder) {
            this.selectedItem = mv;
            return;
          }
        });
      }
    }
  }

  private storeSettings() {
    const settings: any = {
      selectedServiceTruck: this.selectedServiceTruck,
      selectedMovementOrder: this.selectedItem?.id,
    };
    this.componentSelectionStorageService.saveWidgetSelection(this.STORAGE_KEY, this.widgetIdentifier, settings);
  }

  getStyle(): WidgetStyle {
    return WidgetStyle.transparent;
  }
}
